"use client"
import React, { useState } from 'react'
import { motion, AnimatePresence } from "motion/react"
import LangToggle from '@/app/components/LangToggle'
import { pick, type Locale } from '@/lib/i18n'
import { mmDict } from '@/lib/i18n/dicts/mm'

export type MMTabKey = 'home' | 'games' | 'contact' | 'app'

const TABS: { key: MMTabKey; href: string; icon: React.ReactNode }[] = [
    {
        key: 'home', href: '/mm',
        icon: <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-5"><path strokeLinecap="round" strokeLinejoin="round" d="m2.25 12 8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75" /></svg>,
    },
    {
        key: 'games', href: '/mm/gamelist',
        icon: <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-5"><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 0 1 6 3.75h2.25A2.25 2.25 0 0 1 10.5 6v2.25a2.25 2.25 0 0 1-2.25 2.25H6a2.25 2.25 0 0 1-2.25-2.25V6ZM3.75 15.75A2.25 2.25 0 0 1 6 13.5h2.25a2.25 2.25 0 0 1 2.25 2.25V18a2.25 2.25 0 0 1-2.25 2.25H6A2.25 2.25 0 0 1 3.75 18v-2.25ZM13.5 6a2.25 2.25 0 0 1 2.25-2.25H18A2.25 2.25 0 0 1 20.25 6v2.25A2.25 2.25 0 0 1 18 10.5h-2.25a2.25 2.25 0 0 1-2.25-2.25V6ZM13.5 15.75a2.25 2.25 0 0 1 2.25-2.25H18a2.25 2.25 0 0 1 2.25 2.25V18A2.25 2.25 0 0 1 18 20.25h-2.25A2.25 2.25 0 0 1 13.5 18v-2.25Z" /></svg>,
    },
    {
        key: 'contact', href: '/mm/contact',
        icon: <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-5"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z" /></svg>,
    },
    {
        key: 'app', href: '/mm/app',
        icon: <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-5"><path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 0 0 6 3.75v16.5a2.25 2.25 0 0 0 2.25 2.25h7.5a2.25 2.25 0 0 0 2.25-2.25v-16.5a2.25 2.25 0 0 0-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" /></svg>,
    },
]

/** 移动版吸顶导航（毛玻璃 + 汉堡下拉菜单）；locale 可选，默认中文（与 HeaderMenu 契约一致） */
export function MMHeader({ locale = 'zh' }: { locale?: Locale }) {
    const [menuOpen, setMenuOpen] = useState(false)
    const t = pick(mmDict, locale).chrome
    const menuItems: { key: keyof typeof t.menu; href: string }[] = [
        { key: 'games', href: '/mm/gamelist' },
        // 无移动版 cases 页面，先指向移动联系页
        { key: 'cases', href: '/mm/contact' },
        { key: 'developers', href: '/developers' },
        { key: 'contact', href: '/mm/contact' },
        { key: 'app', href: '/mm/app' },
    ]

    return (
        <header className="sticky top-0 z-40 bg-slate-950/80 backdrop-blur-lg border-b border-white/5">
            <div className="flex items-center justify-between px-4 py-3">
                <a href="/mm" className="flex items-center gap-2">
                    <img src="/img/logoapp.png" alt="YYGame" className="h-7 w-auto" />
                    <span className="text-sm font-black text-white tracking-tight">
                        YYGAME<span className="text-indigo-400">.TECH</span>
                    </span>
                </a>
                <button
                    onClick={() => setMenuOpen(!menuOpen)}
                    className="p-2 rounded-lg ring-1 ring-white/10 text-slate-300"
                    aria-label={t.menuAria}
                >
                    {menuOpen ? (
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                        </svg>
                    ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
                        </svg>
                    )}
                </button>
            </div>
            <AnimatePresence>
                {menuOpen && (
                    <motion.nav
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="overflow-hidden border-t border-white/5"
                    >
                        <div className="px-4 py-3 flex flex-col gap-1">
                            {menuItems.map((item) => (
                                <a key={item.key} href={item.href}
                                    onClick={() => setMenuOpen(false)}
                                    className="flex items-center justify-between rounded-xl px-4 py-3 text-sm font-medium text-slate-200 hover:bg-white/5 transition-colors">
                                    {t.menu[item.key]}
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="size-4 text-indigo-400">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
                                    </svg>
                                </a>
                            ))}
                            {/* 语言切换（深色底：用 arbitrary variant 覆盖 LangToggle 浅色文字） */}
                            <div className="mt-1 flex items-center justify-between rounded-xl px-4 py-3 border-t border-white/5">
                                <span className="text-sm font-medium text-slate-200">{t.language}</span>
                                <span className="[&_button]:!text-slate-400 [&_.font-semibold]:!text-white">
                                    <LangToggle locale={locale} />
                                </span>
                            </div>
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    )
}

/** 移动版底部 Tab 栏（毛玻璃，active 高亮）；locale 可选，默认中文 */
export function MMTabBar({ active, locale = 'zh' }: { active: MMTabKey; locale?: Locale }) {
    const t = pick(mmDict, locale).chrome
    return (
        <nav className="fixed bottom-0 inset-x-0 z-40 max-w-md mx-auto bg-slate-950/90 backdrop-blur-lg border-t border-white/10">
            <div className="grid grid-cols-4 px-2 py-2">
                {TABS.map((tab) => (
                    <a key={tab.key} href={tab.href}
                        className={`flex flex-col items-center gap-1 rounded-xl py-1.5 text-[10px] font-medium transition-colors ${active === tab.key ? "text-indigo-400" : "text-slate-500"}`}>
                        {tab.icon}
                        {t.tabs[tab.key]}
                    </a>
                ))}
            </div>
        </nav>
    )
}

export interface MMFooterProps {
    locale?: Locale
}

/** 移动版精简页脚：导航链接列 + 品牌行 + 备案号/51.la 统计脚本；放各页页底 TabBar 之上（pb-28 避让固定 Tab 栏） */
export function MMFooter({ locale = 'zh' }: MMFooterProps) {
    const t = pick(mmDict, locale).footer
    // 运营方案暂无移动版，指向 PC 页 /solutions（与汉堡菜单「开发者 → /developers」同一处理方式）
    const navItems = [
        { key: 'home', href: '/mm' },
        { key: 'games', href: '/mm/gamelist' },
        { key: 'solutions', href: '/solutions' },
        { key: 'contact', href: '/mm/contact' },
    ] as const

    return (
        <footer className="border-t border-white/5 px-5 pt-6 pb-28">
            <nav aria-label="Footer" className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
                {navItems.map((item) => (
                    <a key={item.key} href={item.href}
                        className="text-xs text-slate-400 transition-colors active:text-indigo-300">
                        {t.nav[item.key]}
                    </a>
                ))}
            </nav>
            <p className="mt-4 text-center text-[10px] text-slate-600">{t.tagline}</p>
            {/* 备案号 + 51.la 统计脚本（照 PC 页脚 Footer2View 底栏原样搬运，勿删） */}
            <p className="mt-2 text-center text-[10px] text-slate-600">&copy;YYGAME.TECH <a href='https://beian.miit.gov.cn/' target='_blank' className="transition-colors duration-300 hover:text-indigo-300">沪ICP备10220873号-17</a> 沪公网安备 31010802001500号
            <script id="LA_COLLECT" src="//sdk.51.la/js-sdk-pro.min.js?id=K2vh7LQWRFfA2zk6&ck=K2vh7LQWRFfA2zk6&autoTrack=true&hashMode=true"  defer ></script>

            </p>
        </footer>
    )
}
